import { CommonModule } from '@angular/common';
import { Component, Inject, NgModule, OnDestroy, Optional } from '@angular/core';
import { MAT_SNACK_BAR_DATA, MatSnackBarModule, MatSnackBarRef } from '@angular/material/snack-bar';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { interval, Subject } from 'rxjs';
import { takeUntil, takeWhile } from 'rxjs/operators';

export interface ICountdownSnackbarData {
  message: string;
  actionText: string;
  countdownSeconds: number;
  onRefresh: () => void;
}

@Component({
    selector: 'ts-countdown-snackbar',
    templateUrl: './countdown-snackbar.component.html',
    styleUrls: ['./countdown-snackbar.component.scss'],
    standalone: false
})
export class CountdownSnackbarComponent implements OnDestroy {
  public countdown: number;
  public isHovering = false;
  private isPaused = false;
  private readonly destroy$ = new Subject<void>();
  private countdownSubscription$ = new Subject<void>();

  constructor(
    @Inject(MAT_SNACK_BAR_DATA) public data: ICountdownSnackbarData,
    @Optional() private _snackBarRef: MatSnackBarRef<CountdownSnackbarComponent>
  ) {
    this.countdown = data.countdownSeconds;
    this.startCountdown();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.countdownSubscription$.next();
    this.countdownSubscription$.complete();
  }

  public onButtonMouseEnter(): void {
    this.isHovering = true;
    this.isPaused = true;
    this.countdownSubscription$.next();
  }

  public onButtonMouseLeave(): void {
    this.isHovering = false;
    if (this.isPaused) {
      this.isPaused = false;
      this.startCountdown();
    }
  }

  public onRefreshClick(): void {
    this.data.onRefresh();
    this._snackBarRef?.dismiss();
  }

  private startCountdown(): void {
    this.countdownSubscription$.next();

    interval(1000)
      .pipe(
        takeUntil(this.destroy$),
        takeUntil(this.countdownSubscription$),
        takeWhile(() => !this.isPaused && this.countdown > 0)
      )
      .subscribe({
        next: () => {
          if (this.isPaused) return;

          this.countdown--;

          if (this.countdown <= 0) {
            this.data.onRefresh();
            this._snackBarRef?.dismiss();
            return;
          }
        },
      });
  }
}

@NgModule({
  imports: [CommonModule, MatSnackBarModule, MatButtonModule, TranslateModule],
  declarations: [CountdownSnackbarComponent],
  exports: [CountdownSnackbarComponent],
})
export class CountdownSnackbarComponentModule {}
